/**
 * Overtime Formula Builder — presentation and safe preview only.
 *
 * Payroll still evaluates the saved expression with the backend formula engine
 * (salary_formula_ast). This module never uses eval/Function.
 */

export const OVERTIME_FORMULA_BUILDER_PATH = "/salary-v2/overtime/formula";

export const OT_FORMULA_TEMPLATES = [
  {
    id: "basic",
    label: "Basic Salary-Based OT",
    summary: "Hourly rate from Basic, then × OT hours × OT rate.",
    expression: "(BASIC / WORKING_DAYS / WORKING_HOURS) * OT_HOURS * (OT_PAY_RATE / 100)",
    plain: "(Basic Salary ÷ Working Days ÷ Working Hours) × OT Hours × OT Pay Rate",
  },
  {
    id: "gross",
    label: "Gross Salary-Based OT",
    summary: "Hourly rate from Gross. The sample below pays ₹1,500.",
    expression: "(GROSS / WORKING_DAYS / WORKING_HOURS) * OT_HOURS * (OT_PAY_RATE / 100)",
    plain: "(Gross Salary ÷ Working Days ÷ Working Hours) × OT Hours × OT Pay Rate",
  },
  {
    id: "hourly",
    label: "Hourly Rate-Based OT",
    summary: "Sample hourly rate × OT hours × OT rate.",
    expression: "HOURLY_RATE * OT_HOURS * (OT_PAY_RATE / 100)",
    plain: "Hourly Rate × OT Hours × OT Pay Rate",
  },
  {
    id: "fixed_hour",
    label: "Fixed Amount Per OT Hour",
    summary: "A flat rupee amount for every OT hour.",
    expression: "FIXED_OT_AMOUNT * OT_HOURS",
    plain: "Fixed OT Amount × OT Hours",
  },
  {
    id: "double",
    label: "Double-Pay OT",
    summary: "Twice the regular hourly rate from Gross.",
    expression: "(GROSS / WORKING_DAYS / WORKING_HOURS) * OT_HOURS * 2",
    plain: "(Gross Salary ÷ Working Days ÷ Working Hours) × OT Hours × 2",
  },
  {
    id: "custom",
    label: "Custom Formula",
    summary: "Pick variables and operators, or type your own expression.",
    expression: "",
    plain: "Your expression",
  },
];

export const OT_FORMULA_VARIABLES = [
  { code: "BASIC", label: "Basic Salary", group: "Salary" },
  { code: "GROSS", label: "Gross Salary", group: "Salary" },
  { code: "DA", label: "DA", group: "Salary" },
  { code: "HOURLY_RATE", label: "Hourly Rate", group: "Salary" },
  { code: "FIXED_OT_AMOUNT", label: "Fixed OT Amount", group: "Salary" },
  { code: "OT_HOURS", label: "OT Hours", group: "Overtime" },
  { code: "OT_COUNT", label: "OT Count", group: "Overtime" },
  { code: "OT_DAYS", label: "OT Days", group: "Overtime" },
  { code: "OT_EVENTS", label: "OT Events", group: "Overtime" },
  { code: "OT_PAY_RATE", label: "OT Pay Rate", group: "Overtime" },
  { code: "WORKING_DAYS", label: "Working Days", group: "Calendar" },
  { code: "WORKING_HOURS", label: "Working Hours", group: "Calendar" },
];

export const OT_FORMULA_VARIABLE_CODES = OT_FORMULA_VARIABLES.map((v) => v.code);

export const OT_FORMULA_OPERATORS = [
  { token: "+", label: "+" },
  { token: "-", label: "−" },
  { token: "*", label: "×" },
  { token: "/", label: "÷" },
  { token: "(", label: "(" },
  { token: ")", label: ")" },
];

export const OT_FORMULA_FUNCTIONS = ["min", "max", "round", "if", "percentage"];

/** Documented walkthrough: Gross ₹30,000 / 30 / 10 × 15 hours × 100% = ₹1,500. */
export const OT_FORMULA_EXAMPLE = {
  basic: "18000",
  gross: "30000",
  da: "0",
  hourlyRate: "100",
  fixedAmount: "100",
  otHours: "15",
  otCount: "1",
  otDays: "1",
  otEvents: "1",
  workingDays: "30",
  workingHours: "10",
  otPayRate: "100",
};

const VARIABLE_LABELS = Object.fromEntries(OT_FORMULA_VARIABLES.map((v) => [v.code, v.label]));

const FUNCS = new Set([
  "min",
  "max",
  "if",
  "round",
  "abs",
  "floor",
  "ceil",
  "sum",
  "avg",
  "percentage",
]);

export function roundHalfUp(value, digits = 2) {
  if (!Number.isFinite(value)) return null;
  const factor = 10 ** digits;
  const scaled = value * factor;
  const sign = scaled < 0 ? -1 : 1;
  const rounded = Math.floor(Math.abs(scaled) + 0.5 + 1e-8);
  return (sign * rounded) / factor;
}

export function formatInr(value, { digits = 2 } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

export function normalizeOtExpression(expression) {
  return String(expression || "")
    .replace(/×/g, "*")
    .replace(/÷/g, "/")
    .replace(/−/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

export function templateById(id) {
  return OT_FORMULA_TEMPLATES.find((t) => t.id === id) || null;
}

export function matchOtFormulaTemplate(expression) {
  const norm = normalizeOtExpression(expression).toUpperCase();
  if (!norm) return "custom";
  const hit = OT_FORMULA_TEMPLATES.find(
    (t) => t.id !== "custom" && normalizeOtExpression(t.expression).toUpperCase() === norm
  );
  return hit ? hit.id : "custom";
}

export function readableOtFormula(expression) {
  let text = normalizeOtExpression(expression);
  if (!text) return "No formula yet";
  const codes = Object.keys(VARIABLE_LABELS).sort((a, b) => b.length - a.length);
  for (const code of codes) {
    text = text.replace(new RegExp(`\\b${code}\\b`, "gi"), VARIABLE_LABELS[code]);
  }
  return text
    .replace(/\*/g, "×")
    .replace(/\//g, "÷")
    .replace(/\s+/g, " ")
    .trim();
}

export function sampleToContext(sample = {}) {
  const num = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };
  const basic = num(sample.basic);
  const gross = num(sample.gross);
  const da = num(sample.da);
  const days = num(sample.workingDays);
  const hoursPerDay = num(sample.workingHours);
  const otHours = num(sample.otHours);
  const payRate = num(sample.otPayRate, 100);
  const fixed = num(sample.fixedAmount);
  let hourly = num(sample.hourlyRate);
  if (!(hourly > 0) && days > 0 && hoursPerDay > 0) {
    const base = gross > 0 ? gross : basic;
    hourly = base > 0 ? base / days / hoursPerDay : 0;
  }
  return {
    BASIC: basic,
    GROSS: gross,
    GROSS_SALARY: gross,
    DA: da,
    HOURLY_RATE: hourly,
    HOURLY_WAGE: hourly,
    OT_HOURLY_RATE: hourly,
    FIXED_OT_AMOUNT: fixed,
    OT_FIXED_RATE: fixed,
    OT_HOURS: otHours,
    OT_COUNT: num(sample.otCount, otHours),
    OT_DAYS: num(sample.otDays, otHours > 0 ? 1 : 0),
    OT_EVENTS: num(sample.otEvents, otHours > 0 ? 1 : 0),
    WORKING_DAYS: days,
    WORKING_HOURS: hoursPerDay,
    OT_WORKING_DAYS: days,
    OT_WORKING_HOURS: hoursPerDay,
    OT_PAY_RATE: payRate,
    OT_PAY_RATE_PCT: payRate,
    OT_RATE_MULTIPLIER: 1,
  };
}

function tokenize(source) {
  let s = String(source || "")
    .replace(/×/g, "*")
    .replace(/÷/g, "/")
    .replace(/−/g, "-")
    .replace(/(\d+(?:\.\d+)?)\s*%(?!\s*[\d(])/g, "($1/100)");
  const tokens = [];
  const re = /\s+|(\d+(?:\.\d+)?)|([A-Za-z_][A-Za-z0-9_]*)|(<=|>=|==|!=|&&|\|\||[+\-*/%(),<>])|(.)/g;
  let match;
  while ((match = re.exec(s))) {
    if (!match[1] && !match[2] && !match[3] && !match[4]) continue;
    if (match[1]) tokens.push({ t: "num", v: match[1] });
    else if (match[2]) tokens.push({ t: "id", v: match[2] });
    else if (match[3]) tokens.push({ t: "op", v: match[3] });
    else return { error: `Unexpected character "${match[4]}"` };
  }
  return { tokens };
}

function isWord(token, word) {
  return token && token.t === "id" && token.v.toLowerCase() === word;
}

function parseExpression(tokens) {
  let i = 0;
  const peek = () => tokens[i];

  function parseOr() {
    let node = parseAnd();
    while (isWord(peek(), "or") || (peek() && peek().v === "||")) {
      i += 1;
      node = { type: "or", left: node, right: parseAnd() };
    }
    return node;
  }
  function parseAnd() {
    let node = parseCmp();
    while (isWord(peek(), "and") || (peek() && peek().v === "&&")) {
      i += 1;
      node = { type: "and", left: node, right: parseCmp() };
    }
    return node;
  }
  function parseCmp() {
    let node = parseAdd();
    const ops = new Set([">", "<", ">=", "<=", "==", "!="]);
    while (peek() && peek().t === "op" && ops.has(peek().v)) {
      const op = peek().v;
      i += 1;
      node = { type: "cmp", op, left: node, right: parseAdd() };
    }
    return node;
  }
  function parseAdd() {
    let node = parseMul();
    while (peek() && peek().t === "op" && (peek().v === "+" || peek().v === "-")) {
      const op = peek().v;
      i += 1;
      node = { type: "bin", op, left: node, right: parseMul() };
    }
    return node;
  }
  function parseMul() {
    let node = parseUnary();
    while (peek() && peek().t === "op" && (peek().v === "*" || peek().v === "/" || peek().v === "%")) {
      const op = peek().v;
      i += 1;
      node = { type: "bin", op, left: node, right: parseUnary() };
    }
    return node;
  }
  function parseUnary() {
    if (peek() && peek().t === "op" && (peek().v === "-" || peek().v === "+")) {
      const op = peek().v;
      i += 1;
      return { type: "unary", op, expr: parseUnary() };
    }
    return parsePrimary();
  }
  function parsePrimary() {
    const tok = peek();
    if (!tok) throw new Error("Formula ended unexpectedly");
    if (tok.t === "num") {
      i += 1;
      return { type: "num", v: Number(tok.v) };
    }
    if (tok.t === "op" && tok.v === "(") {
      i += 1;
      const inner = parseOr();
      if (!peek() || peek().v !== ")") throw new Error("Missing closing parenthesis");
      i += 1;
      return inner;
    }
    if (tok.t === "id") {
      const name = tok.v;
      i += 1;
      if (peek() && peek().v === "(") {
        const fn = name.toLowerCase();
        if (!FUNCS.has(fn)) throw new Error(`Unsupported function "${name}"`);
        i += 1;
        const args = [];
        if (!(peek() && peek().v === ")")) {
          args.push(parseOr());
          while (peek() && peek().v === ",") {
            i += 1;
            args.push(parseOr());
          }
        }
        if (!peek() || peek().v !== ")") throw new Error(`Missing closing parenthesis after ${fn}()`);
        i += 1;
        return { type: "call", fn, args };
      }
      return { type: "name", name };
    }
    throw new Error(`Unexpected "${tok.v}"`);
  }

  const ast = parseOr();
  if (i < tokens.length) throw new Error(`Unexpected "${tokens[i].v}"`);
  return ast;
}

function unknownFormulaNames(node, found = new Set()) {
  if (!node || typeof node !== "object") return [...found];
  if (node.type === "name" && !OT_FORMULA_VARIABLE_CODES.includes(String(node.name || "").toUpperCase())) {
    found.add(node.name);
  }
  Object.values(node).forEach((value) => {
    if (Array.isArray(value)) value.forEach((item) => unknownFormulaNames(item, found));
    else if (value && typeof value === "object") unknownFormulaNames(value, found);
  });
  return [...found];
}

function lookup(ctx, name) {
  if (Object.prototype.hasOwnProperty.call(ctx, name)) return Number(ctx[name]) || 0;
  const upper = name.toUpperCase();
  if (Object.prototype.hasOwnProperty.call(ctx, upper)) return Number(ctx[upper]) || 0;
  const hit = Object.keys(ctx).find((k) => k.toLowerCase() === name.toLowerCase());
  return hit ? Number(ctx[hit]) || 0 : 0;
}

function evalNode(node, ctx) {
  if (node.type === "num") return node.v;
  if (node.type === "name") return lookup(ctx, node.name);
  if (node.type === "unary") {
    const v = evalNode(node.expr, ctx);
    return node.op === "-" ? -v : v;
  }
  if (node.type === "bin") {
    const left = evalNode(node.left, ctx);
    const right = evalNode(node.right, ctx);
    if (node.op === "+") return left + right;
    if (node.op === "-") return left - right;
    if (node.op === "*") return left * right;
    if (node.op === "/") {
      if (right === 0) throw new Error("Cannot divide by zero. Check working days, working hours, or a divisor in the formula.");
      return left / right;
    }
    if (node.op === "%") {
      if (right === 0) throw new Error("Cannot divide by zero.");
      return left % right;
    }
  }
  if (node.type === "cmp") {
    const left = evalNode(node.left, ctx);
    const right = evalNode(node.right, ctx);
    const table = {
      ">": left > right,
      "<": left < right,
      ">=": left >= right,
      "<=": left <= right,
      "==": left === right,
      "!=": left !== right,
    };
    return table[node.op] ? 1 : 0;
  }
  if (node.type === "and") return evalNode(node.left, ctx) && evalNode(node.right, ctx) ? 1 : 0;
  if (node.type === "or") return evalNode(node.left, ctx) || evalNode(node.right, ctx) ? 1 : 0;
  if (node.type === "call") {
    const args = node.args.map((a) => evalNode(a, ctx));
    const fn = node.fn;
    if ((fn === "min" || fn === "max") && args.length !== 2) throw new Error(`${fn}() expects 2 values`);
    if (fn === "if" && args.length !== 3) throw new Error("if() expects condition, value if true, value if false");
    if (fn === "percentage" && args.length !== 2) throw new Error("percentage() expects base and rate");
    if ((fn === "abs" || fn === "floor" || fn === "ceil") && args.length !== 1) throw new Error(`${fn}() expects 1 value`);
    if (fn === "round" && (args.length < 1 || args.length > 2)) throw new Error("round() expects 1 or 2 values");
    if ((fn === "sum" || fn === "avg") && args.length < 1) throw new Error(`${fn}() expects at least 1 value`);
    if (fn === "min") return Math.min(args[0], args[1]);
    if (fn === "max") return Math.max(args[0], args[1]);
    if (fn === "if") return args[0] ? args[1] : args[2];
    if (fn === "abs") return Math.abs(args[0]);
    if (fn === "floor") return Math.floor(args[0]);
    if (fn === "ceil") return Math.ceil(args[0]);
    if (fn === "round") {
      const digits = args.length === 2 ? args[1] : 0;
      return roundHalfUp(args[0], digits);
    }
    if (fn === "sum") return args.reduce((s, n) => s + n, 0);
    if (fn === "avg") return args.reduce((s, n) => s + n, 0) / args.length;
    if (fn === "percentage") return (args[0] * args[1]) / 100;
  }
  throw new Error("This formula could not be calculated");
}

export function evaluateOtFormula(expression, context = {}) {
  const raw = String(expression || "").trim();
  if (!raw) return { ok: false, value: null, error: "Enter a formula." };
  if (raw.length > 500) return { ok: false, value: null, error: "Formula is too long (maximum 500 characters)." };
  const lex = tokenize(raw);
  if (lex.error) return { ok: false, value: null, error: lex.error };
  if (!lex.tokens.length) return { ok: false, value: null, error: "Enter a formula." };
  try {
    const ast = parseExpression(lex.tokens);
    const unknown = unknownFormulaNames(ast);
    if (unknown.length) {
      return { ok: false, value: null, error: `Unknown field ${unknown.join(", ")}. Use the overtime variables listed below.` };
    }
    const value = evalNode(ast, context);
    if (!Number.isFinite(value)) return { ok: false, value: null, error: "Formula did not produce a number." };
    return { ok: true, value: roundHalfUp(value, 2), error: null };
  } catch (err) {
    return { ok: false, value: null, error: err?.message || "Formula could not be calculated." };
  }
}

function moneyStep(label, value, extra = {}) {
  return { label, value, ...extra };
}

export function explainOtCalculation(templateId, context, result) {
  const ctx = context || {};
  const days = Number(ctx.WORKING_DAYS) || 0;
  const hpd = Number(ctx.WORKING_HOURS) || 0;
  const hours = Number(ctx.OT_HOURS) || 0;
  const rate = Number(ctx.OT_PAY_RATE) || 0;
  const factor = rate / 100;
  const hourlyFrom = (base) => (days > 0 && hpd > 0 ? base / days / hpd : null);

  if (templateId === "gross" || templateId === "basic" || templateId === "double") {
    const base = templateId === "basic" ? Number(ctx.BASIC) || 0 : Number(ctx.GROSS) || 0;
    const baseLabel = templateId === "basic" ? "Basic Salary" : "Gross Salary";
    const daily = days > 0 ? base / days : null;
    const hourly = hourlyFrom(base);
    const multiplier = templateId === "double" ? 2 : factor;
    const rateLabel = templateId === "double" ? "Double pay" : "OT pay rate";
    const rateValue = templateId === "double" ? "2 × regular hourly rate" : `${rate}% = × ${factor.toFixed(2)}`;
    const amount =
      hourly == null
        ? "Working days and working hours must be greater than zero."
        : `${formatInr(hourly)} × ${hours} × ${multiplier} = ${formatInr(result)}`;
    return [
      moneyStep(baseLabel, formatInr(base), { note: "Sample salary for this preview. Payroll uses the employee’s salary." }),
      moneyStep("Working days", String(days)),
      moneyStep("Working hours / day", String(hpd)),
      moneyStep("Step 1 · Daily rate", daily == null ? "—" : `${formatInr(base)} ÷ ${days} = ${formatInr(daily)}`),
      moneyStep("Step 2 · Regular hourly rate", hourly == null ? "—" : `${formatInr(daily)} ÷ ${hpd} = ${formatInr(hourly)}`),
      moneyStep("OT hours", String(hours), { note: "Payroll uses approved OT hours from Attendance." }),
      moneyStep(rateLabel, rateValue),
      moneyStep("Step 3 · OT amount", amount, { emphasize: true }),
    ];
  }

  if (templateId === "hourly") {
    const hourly = Number(ctx.HOURLY_RATE) || 0;
    return [
      moneyStep("Hourly rate", formatInr(hourly), { note: "In payroll this is Gross (or Basic) ÷ working days ÷ working hours when the employee has no stored hourly rate." }),
      moneyStep("OT hours", String(hours)),
      moneyStep("OT pay rate", `${rate}% = × ${factor.toFixed(2)}`),
      moneyStep("OT amount", `${formatInr(hourly)} × ${hours} × ${factor.toFixed(2)} = ${formatInr(result)}`, { emphasize: true }),
    ];
  }

  if (templateId === "fixed_hour") {
    const fixed = Number(ctx.FIXED_OT_AMOUNT) || 0;
    return [
      moneyStep("Fixed amount per OT hour", formatInr(fixed)),
      moneyStep("OT hours", String(hours)),
      moneyStep("OT amount", `${formatInr(fixed)} × ${hours} = ${formatInr(result)}`, { emphasize: true }),
    ];
  }

  const used = OT_FORMULA_VARIABLES.filter((v) => new RegExp(`\\b${v.code}\\b`, "i").test(String(context.__expression || "")));
  const rows = used.map((v) => moneyStep(v.label, formatVariable(v.code, ctx)));
  rows.push(moneyStep("OT amount", formatInr(result), { emphasize: true }));
  return rows;
}

function formatVariable(code, ctx) {
  const n = Number(ctx[code]);
  if (!Number.isFinite(n)) return "—";
  if (code === "OT_PAY_RATE" || code === "OT_PAY_RATE_PCT") return `${n}%`;
  if (["WORKING_DAYS", "WORKING_HOURS", "OT_HOURS", "OT_COUNT", "OT_DAYS", "OT_EVENTS"].includes(code)) {
    return String(n);
  }
  return formatInr(n);
}

export function explainWithExpression(templateId, expression, context, result) {
  return explainOtCalculation(templateId, { ...context, __expression: expression }, result);
}
